import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import TrashIcon from "./TrashIcon";
import "./CheckinForm.css";
import "./RoomForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const CONDITIONS = [
  { value: "good", label: "Good" },
  { value: "insufficient", label: "Insufficient" },
  { value: "poor", label: "Poor" },
];

const SAVE_DEBOUNCE_MS = 800;

const formatTime = (date) =>
  date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

const RoomForm = () => {
  const { cfid, roomId } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: saveRoomRequest,
    isLoading: isSaving,
    error: saveError,
  } = useHttpNoAuth();
  const {
    sendRequest: uploadPhotosRequest,
    isLoading: isUploading,
    error: uploadError,
  } = useHttpNoAuth();
  const {
    sendRequest: deletePhotoRequest,
    isLoading: isDeletingPhoto,
    error: deletePhotoError,
  } = useHttpNoAuth();
  const {
    sendRequest: deleteRoomRequest,
    isLoading: isDeleting,
    error: deleteError,
  } = useHttpNoAuth();

  const [address, setAddress] = useState("");
  const [room, setRoom] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [condition, setCondition] = useState(null);
  const [comments, setComments] = useState("");
  const [hasDamage, setHasDamage] = useState(false);
  const [damageComments, setDamageComments] = useState("");

  const [savedAt, setSavedAt] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  // How many thumbs to hold open while the upload is in flight.
  const [uploadingCount, setUploadingCount] = useState(0);

  // What the server currently holds — nothing is sent while the form matches it.
  const baselineRef = useRef(null);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const loadRoom = useCallback((onLoaded) => {
    if (!cfid || !roomId) return;
    getCheckinRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
      (data) => {
        onLoaded?.();
        const found = (data?.rooms ?? []).find(
          (item) => String(item.id) === String(roomId)
        );

        setAddress(
          [data?.street, data?.number, data?.floor].filter(Boolean).join(" ")
        );

        if (!found) {
          setNotFound(true);
          return;
        }

        setNotFound(false);
        setRoom(found);
        setCondition(found.condition ?? null);
        setComments(found.comments ?? "");
        setHasDamage(Boolean(found.has_damage));
        setDamageComments(found.damage_comments ?? "");
        baselineRef.current = {
          condition: found.condition ?? null,
          comments: found.comments ?? "",
          hasDamage: Boolean(found.has_damage),
          damageComments: found.damage_comments ?? "",
        };
      }
    );
  }, [cfid, roomId, getCheckinRequest]);

  useEffect(() => {
    loadRoom();
  }, [loadRoom]);

  // Recomputed every render: the baseline lives in a ref and moves on each save.
  const base = baselineRef.current;
  const isDirty = Boolean(
    base &&
      (base.condition !== condition ||
        base.comments !== comments ||
        base.hasDamage !== hasDamage ||
        base.damageComments !== damageComments)
  );

  const saveRoom = useCallback(() => {
    const sent = { condition, comments, hasDamage, damageComments };
    return saveRoomRequest(
      {
        url: `${BACKEND_URL}/welocate/rooms/${roomId}`,
        method: "PATCH",
        body: {
          condition: sent.condition,
          comments: sent.comments,
          has_damage: sent.hasDamage,
          damage_comments: sent.damageComments,
        },
      },
      () => {
        baselineRef.current = sent;
        setSavedAt(new Date());
      }
    );
  }, [condition, comments, hasDamage, damageComments, roomId, saveRoomRequest]);

  // Autosave: the design has no save button for the fields, only a "done" exit.
  useEffect(() => {
    if (!isDirty) return undefined;
    const timer = setTimeout(saveRoom, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isDirty, saveRoom]);

  const roomName = room?.name ?? "Room";
  const photos = Array.isArray(room?.photos) ? room.photos : [];

  const uploadPhotos = (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    const formData = new FormData();
    formData.append("checkin_id", cfid);
    files.forEach((file) => formData.append("files", file));

    setUploadingCount(files.length);
    uploadPhotosRequest(
      {
        url: `${BACKEND_URL}/welocate/rooms/${roomId}/photos`,
        method: "POST",
        body: formData,
        isFileUpload: true,
      },
      () => {
        setSavedAt(new Date());
        // The skeletons stay up over the refetch, so they hand straight over
        // to the real thumbs instead of blinking out in between.
        loadRoom(() => setUploadingCount(0));
      }
    );
  };

  // The hook never rejects, so a failed upload — or a failed refetch after a
  // good one — has to clear the skeletons here.
  useEffect(() => {
    if (uploadError || error) setUploadingCount(0);
  }, [uploadError, error]);

  const pickPhotos = (event) => {
    uploadPhotos(event.target.files);
    // Let the same file be picked again after an upload or a cancel.
    event.target.value = "";
  };

  const deletePhoto = (photoId) => {
    if (isDeletingPhoto) return;
    setDeletingPhotoId(photoId);
    deletePhotoRequest(
      { url: `${BACKEND_URL}/welocate/photos/${photoId}`, method: "DELETE" },
      () => {
        setDeletingPhotoId(null);
        // Drop the thumb straight away — the room holds no other photo state.
        setRoom((current) =>
          current
            ? {
                ...current,
                photos: (current.photos ?? []).filter(
                  (photo) => photo.id !== photoId
                ),
              }
            : current
        );
        setSavedAt(new Date());
      }
    );
  };

  // The hook never rejects, so a failed delete has to release the thumb here.
  useEffect(() => {
    if (deletePhotoError) setDeletingPhotoId(null);
  }, [deletePhotoError]);

  const deleteRoom = () => {
    if (isDeleting) return;
    deleteRoomRequest(
      { url: `${BACKEND_URL}/welocate/rooms/${roomId}`, method: "DELETE" },
      () => navigate(`/checkin/${cfid}`)
    );
  };

  const finish = async () => {
    if (isDirty) await saveRoom();
    navigate(`/checkin/${cfid}`);
  };

  const isBusy = isSaving || isUploading;
  const hasFailed = !isBusy && Boolean(saveError || uploadError);

  // Edits are only in the browser until the debounce fires and the PATCH lands,
  // so the way out stays shut until then. A failed save reopens it — the error
  // is on screen and stranding the user on the page helps nobody.
  const isSavePending = (isDirty || isSaving) && !saveError;

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        <button
          type="button"
          className="cf-back"
          aria-label={isSavePending ? "Saving — please wait" : "Back"}
          aria-busy={isSavePending}
          disabled={isSavePending}
          onClick={() => navigate(`/checkin/${cfid}`)}
        >
          {isSavePending ? <span className="cf-spinner" /> : <ChevronLeftIcon />}
        </button>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">{room ? roomName : "Room"}</h1>
          <p className="cf-topbar-sub">{address}</p>
        </div>

        {(isBusy || hasFailed || savedAt) && (
          <span
            className={`cf-saved${hasFailed ? " rf-saved--error" : ""}`}
            role="status"
          >
            {isBusy && "Saving…"}
            {hasFailed && "Not saved"}
            {!isBusy && !hasFailed && (
              <>
                <CheckCircleOutlineIcon />
                Saved <span className="cf-saved-time">{formatTime(savedAt)}</span>
              </>
            )}
          </span>
        )}
      </header>

      {isLoading && !room && <p className="cf-state">Loading room…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={loadRoom}>
            Try again
          </button>
        </div>
      )}

      {!isLoading && !error && notFound && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">This room is no longer part of the check-in.</p>
          <button
            type="button"
            className="cf-state-retry"
            onClick={() => navigate(`/checkin/${cfid}`)}
          >
            Back to check-in
          </button>
        </div>
      )}

      {room && (
        <main className="cf-main">
          <section className="cf-card rf-block">
            <h2 className="rf-label">Condition</h2>
            <div
              className="rf-choices rf-choices--rating"
              role="group"
              aria-label="Condition"
            >
              {CONDITIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={condition === option.value}
                  className={`rf-choice${
                    condition === option.value
                      ? ` rf-choice--on rf-choice--${option.value}`
                      : ""
                  }`}
                  onClick={() =>
                    setCondition((current) =>
                      current === option.value ? null : option.value
                    )
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>

            <h2 className="rf-label">Notes</h2>
            <textarea
              className="rf-notes"
              placeholder={`State of the ${roomName.toLowerCase()}…`}
              value={comments}
              onChange={(event) => setComments(event.target.value)}
            />
            <p className="rf-help">
              Anything the tenant should not be charged for later.
            </p>
          </section>

          <section className="cf-card rf-block">
            <div className="rf-photos-head">
              <h3 className="cf-section-title">{roomName.toUpperCase()} — PHOTOS</h3>
              <span className="rf-photos-count">
                {uploadingCount > 0
                  ? `Uploading ${uploadingCount}…`
                  : `${photos.length} stored`}
              </span>
            </div>

            <div className="rf-choices">
              <button
                type="button"
                className="rf-photo-btn"
                disabled={isUploading}
                onClick={() => cameraInputRef.current?.click()}
              >
                <PhotoCameraOutlinedIcon />
                Take photos
              </button>
              <button
                type="button"
                className="rf-photo-btn"
                disabled={isUploading}
                onClick={() => libraryInputRef.current?.click()}
              >
                <CollectionsOutlinedIcon />
                Library
              </button>
            </div>

            <input
              ref={cameraInputRef}
              className="rf-file-input"
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={pickPhotos}
            />
            <input
              ref={libraryInputRef}
              className="rf-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={pickPhotos}
            />

            {(photos.length > 0 || uploadingCount > 0) && (
              <ul className="rf-thumbs">
                {photos.map((photo) => (
                  <li key={photo.id} className="rf-thumb">
                    <img src={photo.photo_blob_url} alt={photo.comments || roomName} />
                    <span className="rf-thumb-stored" aria-label="Uploaded">
                      <CheckIcon />
                    </span>
                    <button
                      type="button"
                      className="rf-thumb-delete"
                      aria-label={`Delete photo of ${roomName}`}
                      disabled={isDeletingPhoto}
                      onClick={() => deletePhoto(photo.id)}
                    >
                      {deletingPhotoId === photo.id ? (
                        <span className="cf-spinner rf-thumb-spinner" />
                      ) : (
                        <CloseIcon />
                      )}
                    </button>
                  </li>
                ))}

                {Array.from({ length: uploadingCount }, (_, index) => (
                  <li
                    key={`pending-${index}`}
                    className="rf-thumb rf-thumb--pending"
                    aria-label="Uploading photo"
                  />
                ))}
              </ul>
            )}

            {!isUploading && uploadError && (
              <p className="rf-error" role="alert">
                {uploadError}
              </p>
            )}

            {!isDeletingPhoto && deletePhotoError && (
              <p className="rf-error" role="alert">
                {deletePhotoError}
              </p>
            )}
          </section>

          <div className={`rf-damages-box${hasDamage ? " rf-damages-box--on" : ""}`}>
            <label className="rf-damages">
              <input
                type="checkbox"
                className="rf-checkbox"
                checked={hasDamage}
                onChange={(event) => {
                  setHasDamage(event.target.checked);
                  // The field disappears with the tick, so its text goes too.
                  if (!event.target.checked) setDamageComments("");
                }}
              />
              <span className="rf-damages-body">
                <span className="rf-damages-title">Damages</span>
                <span className="rf-damages-sub">Tick if there is any damage here</span>
              </span>
            </label>

            {hasDamage && (
              <div className="rf-damages-detail">
                <label className="rf-label" htmlFor="rf-damage-comments">
                  What is damaged?
                </label>
                <textarea
                  id="rf-damage-comments"
                  className="rf-notes rf-notes--damage"
                  placeholder="Describe the damage…"
                  value={damageComments}
                  onChange={(event) => setDamageComments(event.target.value)}
                />
              </div>
            )}
          </div>

          {confirmingDelete ? (
            <div className="rf-delete-confirm">
              <button
                type="button"
                className="rf-delete rf-delete--solid"
                disabled={isDeleting}
                onClick={deleteRoom}
              >
                {isDeleting ? "Deleting…" : `Yes, delete ${roomName}`}
              </button>
              <button
                type="button"
                className="rf-delete-cancel"
                disabled={isDeleting}
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="rf-delete"
              onClick={() => setConfirmingDelete(true)}
            >
              <TrashIcon />
              Delete this room
            </button>
          )}

          <p className="rf-delete-help">
            Deleting removes the room from the report along with its notes and photos.
          </p>

          {!isDeleting && deleteError && (
            <p className="rf-error" role="alert">
              {deleteError}
            </p>
          )}

          {!isSaving && saveError && (
            <p className="rf-error" role="alert">
              {saveError}
            </p>
          )}
        </main>
      )}

      {room && (
        <footer className="cf-actions">
          <div className="cf-actions-inner">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              disabled={isSaving}
              onClick={finish}
            >
              <CheckIcon />
              Done with {roomName}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default RoomForm;
